import { useState, useEffect } from "react";
import MainStatCard from "../../../components/analytics/MainStatCard";
import SectionAnalytics from "../../../components/analytics/SectionAnalytics";
import AutogestionDonutChart from "../../../components/analytics/AutogestionDonutChart";
import AutogestionDailyChart from "../../../components/analytics/AutogestionDailyChart";
import OperativeEvolutionChart from "../../../components/analytics/OperativeEvolutionChart";
import CategoryBarChartCard from "../../../components/analytics/CategoryBarChartCard";
import AdvisorPerformanceTable from "../../../components/analytics/AdvisorPerformanceTable";
import { ChartSkeleton, StatSkeleton } from "../../../components/analytics/ChartSkeleton";
import { trpc } from "../../../trpc/index";
import { formatDecimal, formatDurationHM } from "../../../utils";
import { mapTicketStatus } from "../../../utils/valueMap";

type Props = {
  startDate: string;
  endDate: string;
  merchantId: string;
};

const AnalyticsAdminContainer = ({ startDate, endDate, merchantId }: Props) => {
  const [fetching, setFetching] = useState(true);

  const rangeInput = { merchantId, startDate, endDate };

  const { data: volumeData, refetch: refetchVolume } =
    trpc.analytics.adminConversationVolume.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        setFetching(false);
      },
      onError: () => {
        setFetching(false);
      },
    });

  const { data: autogestionData, refetch: refetchAutogestion } =
    trpc.analytics.adminAutogestionRate.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: autogestionDailyData, refetch: refetchAutogestionDaily } =
    trpc.analytics.adminAutogestionDaily.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: ticketsDailyData, refetch: refetchTicketsDaily } =
    trpc.analytics.adminTicketsDaily.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: escalationByCategoryData, refetch: refetchEscalationByCategory } =
    trpc.analytics.adminEscalationByCategory.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: ticketStatusData, refetch: refetchTicketStatus } =
    trpc.analytics.adminTicketStatus.useQuery(
      { ...rangeInput, pipelineId: "general" },
      { enabled: false, refetchOnWindowFocus: false }
    );

  const { data: ticketAlertsData, refetch: refetchTicketAlerts } =
    trpc.analytics.adminTicketAlerts.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: advisorPerformanceData, refetch: refetchAdvisorPerformance } =
    trpc.analytics.adminAdvisorPerformance.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  const { data: resolutionTimeData, refetch: refetchResolutionTime } =
    trpc.analytics.adminResolutionTime.useQuery(rangeInput, {
      enabled: false,
      refetchOnWindowFocus: false,
    });

  // Ticket statuses are merchant-configurable pipeline ticketPositions —
  // resolve display names from config (mapTicketStatus is only a fallback).
  const { data: ticketPositionsByStage } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId, pipelineId: "general" },
    { enabled: !!merchantId, refetchOnWindowFocus: false }
  );
  const ticketStatusLabels = Object.fromEntries(
    Object.values(ticketPositionsByStage ?? {})
      .flat()
      .map((position) => [position.id, position.displayName])
  );

  useEffect(() => {
    if (merchantId && startDate && endDate) {
      setFetching(true);

      refetchVolume();
      refetchAutogestion();
      refetchAutogestionDaily();
      refetchTicketsDaily();
      refetchEscalationByCategory();
      refetchTicketStatus();
      refetchTicketAlerts();
      refetchAdvisorPerformance();
      refetchResolutionTime();
    }
  }, [merchantId, startDate, endDate]);

  if (fetching) {
    return (
      <div className="flex h-full grow flex-col gap-6">
        <SectionAnalytics title="Resumen" gridClassName="grid-cols-1 md:grid-cols-3">
          <StatSkeleton />
          <StatSkeleton />
          <StatSkeleton />
        </SectionAnalytics>
        <SectionAnalytics title="Autogestión" gridClassName="grid-cols-1 md:grid-cols-2">
          <ChartSkeleton height="h-[260px]" />
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
        <SectionAnalytics title="Evolución operativa" gridClassName="grid-cols-1">
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
        <SectionAnalytics title="Análisis del escalado" gridClassName="grid-cols-1 md:grid-cols-2">
          <ChartSkeleton height="h-[260px]" />
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
        <SectionAnalytics title="Carga y desempeño por asesor" gridClassName="grid-cols-1">
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
      </div>
    );
  }

  const totalConversations = volumeData?.totalConversations ?? 0;
  const totalTickets = volumeData?.totalTickets ?? 0;
  const autogestionRate = autogestionData?.autogestionRate ?? 0;
  const avgResolutionSeconds = resolutionTimeData?.avgResolutionSeconds ?? null;
  const blockedCount = ticketAlertsData?.blockedCount ?? 0;
  const staleNewCount = ticketAlertsData?.staleNewCount ?? 0;

  const escalationChartData = (escalationByCategoryData ?? []).map((row) => ({
    name: row.category,
    value: row.count,
  }));
  const ticketStatusChartData = (ticketStatusData ?? []).map((row) => ({
    name: ticketStatusLabels[row.status] ?? mapTicketStatus[row.status] ?? row.status,
    value: row.count,
  }));

  return (
    <div className="flex h-full grow flex-col gap-6">
      <SectionAnalytics title="Resumen" gridClassName="grid-cols-1 md:grid-cols-3">
        <MainStatCard
          title="Volumen de conversaciones"
          value={totalConversations}
          subtitle={`${totalTickets} ${totalTickets === 1 ? "solicitud creada" : "solicitudes creadas"}`}
          info="Conversaciones del embudo administrativo iniciadas en el período y solicitudes generadas a partir de ellas"
        />
        <MainStatCard
          title="Tasa de autogestión"
          value={`${formatDecimal(autogestionRate, 0)}%`}
          info="Porcentaje de conversaciones resueltas sin intervención de un asesor humano"
        />
        <MainStatCard
          title="Tiempo de resolución"
          value={avgResolutionSeconds === null ? "—" : formatDurationHM(avgResolutionSeconds)}
          subtitle="Respuesta IA: instantánea"
          info="Tiempo promedio entre la creación y el cierre de las solicitudes resueltas por asesores"
        />
      </SectionAnalytics>

      <SectionAnalytics title="Autogestión" gridClassName="grid-cols-1 md:grid-cols-2">
        <AutogestionDonutChart
          info="Cómo terminaron las conversaciones del período: gestionadas por la IA de inicio a fin (con o sin solicitud), todavía en curso, o escaladas a un asesor humano"
          data={{
            paqinfoCount: autogestionData?.paqinfoCount ?? 0,
            aiResolvedCount: autogestionData?.aiResolvedCount ?? 0,
            enCursoCount: autogestionData?.enCursoCount ?? 0,
            escaladoCount: autogestionData?.escaladoCount ?? 0,
            totalConversations: autogestionData?.totalConversations ?? 0,
            autogestionRate,
          }}
        />
        <AutogestionDailyChart
          info="Porcentaje de las conversaciones iniciadas cada día que la IA gestionó sin intervención humana. Los días sin conversaciones no muestran punto"
          data={autogestionDailyData ?? []}
        />
      </SectionAnalytics>

      <SectionAnalytics title="Evolución operativa" gridClassName="grid-cols-1">
        <OperativeEvolutionChart
          title="Volumen diario"
          subtitle="Conversaciones y solicitudes creadas por día"
          info="Las series se apilan una sobre otra: el borde superior es el total del día y cada color muestra su parte. Cambiá entre conversaciones (según cómo se resolvieron) y solicitudes (según quién las gestiona: la IA o un asesor)"
          conversationsData={autogestionDailyData ?? []}
          ticketsData={ticketsDailyData ?? []}
        />
      </SectionAnalytics>

      <SectionAnalytics title="Análisis del escalado" gridClassName="grid-cols-1 md:grid-cols-2">
        {(blockedCount > 0 || staleNewCount > 0) && (
          <div className="flex flex-wrap gap-2 md:col-span-2">
            {blockedCount > 0 && (
              <span className="rounded-full bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700">
                {blockedCount}{" "}
                {blockedCount === 1 ? "solicitud bloqueada" : "solicitudes bloqueadas"}
              </span>
            )}
            {staleNewCount > 0 && (
              <span className="rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700">
                {staleNewCount}{" "}
                {staleNewCount === 1
                  ? "solicitud sin gestionar hace más de 48h"
                  : "solicitudes sin gestionar hace más de 48h"}
              </span>
            )}
          </div>
        )}
        <CategoryBarChartCard
          title="Escalado por área"
          subtitle="Solicitudes escaladas a un asesor, por categoría"
          info="Solicitudes gestionadas por un asesor en el período, agrupadas por su categoría de transferencia (configurable en Asignación)"
          data={escalationChartData}
          orientation="horizontal"
          emptyText="Sin datos en este período"
          unitLabel={{ singular: "solicitud", plural: "solicitudes" }}
        />
        <CategoryBarChartCard
          title="Estado de tickets"
          subtitle="Solicitudes del período según su estado actual"
          info="Todas las solicitudes del período según el estado en que están hoy — tanto las que la IA resolvió directamente como las escaladas a asesores"
          data={ticketStatusChartData}
          orientation="horizontal"
          emptyText="Sin datos en este período"
          unitLabel={{ singular: "solicitud", plural: "solicitudes" }}
        />
      </SectionAnalytics>

      <SectionAnalytics title="Carga y desempeño por asesor" gridClassName="grid-cols-1">
        <AdvisorPerformanceTable
          info="Solo solicitudes gestionadas por asesores (las de la IA no cuentan). Resueltas = cerradas con éxito; el tiempo medio y el % de cierre se calculan sobre las solicitudes ya cerradas"
          rows={advisorPerformanceData ?? []}
        />
      </SectionAnalytics>
    </div>
  );
};

export default AnalyticsAdminContainer;
